/**
 * Toque de chamada Multiflow Voz (mesmo áudio do WaVoIP).
 */
import SoundCalling from "../components/WavoipCall/calling.mp3";
import SoundRinging from "../components/WavoipCall/ring.mp3";

let ringingAudio = null;
let callingAudio = null;
let unlockEl = null;
/** Se true, retoma o toque no próximo gesto do usuário (autoplay bloqueado). */
let pendingRing = false;
let gestureBound = false;
/** Epoch: each stop() bumps so delayed play() calls become no-ops. */
let ringEpoch = 0;
/** When true, pending autoplay resume must not restart ring (after answer). */
let suppressRing = false;

function ensureUnlockEl() {
  if (unlockEl) return unlockEl;
  unlockEl = new Audio(SoundRinging);
  unlockEl.volume = 0.01;
  unlockEl.preload = "auto";
  return unlockEl;
}

function isPlaying(audio) {
  return Boolean(audio && !audio.paused && !audio.ended && audio.currentTime >= 0);
}

/** Call after Atender / call-started so gesture unlock cannot re-arm the ring. */
export function suppressVoiceRing(suppressed = true) {
  suppressRing = Boolean(suppressed);
  if (suppressRing) {
    pendingRing = false;
  }
}

function ensureGestureUnlock() {
  if (gestureBound || typeof window === "undefined") return;
  gestureBound = true;
  const resume = () => {
    unlockVoiceAudio();
    // bubble phase: Atender já pode ter marcado suppress antes deste resume
    if (pendingRing && !suppressRing) {
      playRinging().catch(() => {});
    }
  };
  // bubble (não capture): evita rearmar o toque no mesmo clique de Atender
  window.addEventListener("pointerdown", resume, { capture: false });
  window.addEventListener("keydown", resume, { capture: false });
  window.addEventListener("touchstart", resume, { capture: false });
}

export function unlockVoiceAudio() {
  ensureGestureUnlock();
  const el = ensureUnlockEl();
  el.play()
    .then(() => {
      el.pause();
      el.currentTime = 0;
    })
    .catch(() => {});
}

export function stopRinging() {
  pendingRing = false;
  ringEpoch += 1;
  if (!ringingAudio) return;
  try {
    ringingAudio.pause();
    ringingAudio.currentTime = 0;
  } catch {
    /* ignore */
  }
  ringingAudio = null;
}

export function stopCalling() {
  if (!callingAudio) return;
  try {
    callingAudio.pause();
    callingAudio.currentTime = 0;
  } catch {
    /* ignore */
  }
  callingAudio = null;
}

export function stopAllVoiceSounds() {
  stopRinging();
  stopCalling();
}

/** Para toque e impede rearme até clear (Atender / call-started). */
export function stopAllVoiceSoundsAndSuppress() {
  suppressVoiceRing(true);
  stopAllVoiceSounds();
}

/** true se o ring já está rodando em loop (evita “engasgo” ao recomeçar o mp3). */
export function isVoiceRingingPlaying() {
  return isPlaying(ringingAudio);
}

/**
 * @returns {Promise<"playing"|"blocked">}
 */
export async function playRinging() {
  if (suppressRing) {
    pendingRing = false;
    return "blocked";
  }
  // Já em loop: NÃO recria o Audio (socket/effect/softphone disparavam em sequência → engasgo)
  if (isPlaying(ringingAudio)) {
    pendingRing = false;
    return "playing";
  }
  ensureGestureUnlock();
  stopRinging();
  const epoch = ringEpoch;
  unlockVoiceAudio();
  const audio = new Audio(SoundRinging);
  audio.loop = true;
  audio.volume = 1;
  ringingAudio = audio;
  try {
    await audio.play();
    // stop() during await, or newer play() — kill this instance
    if (ringEpoch !== epoch || ringingAudio !== audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch {
        /* ignore */
      }
      if (ringingAudio === audio) ringingAudio = null;
      return "blocked";
    }
    pendingRing = false;
    return "playing";
  } catch (err) {
    console.warn("[voz] ringing bloqueado pelo navegador:", err?.message || err);
    if (ringingAudio === audio) ringingAudio = null;
    if (ringEpoch === epoch && !suppressRing) pendingRing = true;
    return "blocked";
  }
}

/**
 * @returns {Promise<"playing"|"blocked">}
 */
export async function playCalling() {
  if (isPlaying(callingAudio)) {
    return "playing";
  }
  ensureGestureUnlock();
  stopCalling();
  unlockVoiceAudio();
  const audio = new Audio(SoundCalling);
  audio.loop = true;
  audio.volume = 1;
  callingAudio = audio;
  try {
    await audio.play();
    return "playing";
  } catch (err) {
    console.warn("[voz] calling bloqueado pelo navegador:", err?.message || err);
    return "blocked";
  }
}
